import * as React from "react";
import { Page, Card, Table, Alert, Text, Button, Grid, Form } from "tabler-react";
import TradesSiteWrapper from "./TradesSiteWrapper.react";
import RefreshButton from "./RefreshButton.react";
import { getTrades, todayIST, fmtDateTime, fmtCurrency, fmtAvgPnl, pnlClass } from "./api";

class TradesPage extends React.Component {
  state = {
    loading: true,
    error: "",
    data: null,
    dateFrom: "",
    dateTo: "",
    view: "closed", // closed | fills
    strategyId: "",
  };

  componentDidMount() {
    const params = new URLSearchParams(window.location.search);
    const view = params.get("view") === "fills" ? "fills" : "closed";
    const today = todayIST();
    this.setState(
      {
        dateFrom: params.get("date_from") || today,
        dateTo: params.get("date_to") || today,
        view,
        strategyId: params.get("strategy") || "",
      },
      this.load
    );
  }

  load = async () => {
    const { dateFrom, dateTo } = this.state;
    this.setState({ loading: true, error: "" });
    try {
      const query = { limit: 2000 };
      if (dateFrom) query.date_from = dateFrom;
      if (dateTo) query.date_to = dateTo;
      const resp = await getTrades(query);
      this.setState({ data: resp, loading: false });
    } catch (e) {
      this.setState({ error: e.message || "Failed to load trades", loading: false });
    }
  };

  setToday = () => {
    const today = todayIST();
    this.setState({ dateFrom: today, dateTo: today }, this.load);
  };

  clearDates = () => {
    this.setState({ dateFrom: "", dateTo: "" }, this.load);
  };

  renderSummary(data) {
    if (!data) return null;
    return (
      <Text className="text-muted mb-3">
        Showing <strong>{data.trades || 0}</strong> closed
        {data.filtered_total !== data.total ? ` (${data.total} fill rows in log)` : ""}. Totals:{" "}
        {data.wins || 0} wins, {data.losses || 0} losses, win {Number(data.win_rate || 0).toFixed(1)}%, avg
        win <span className={pnlClass(data.avg_win || 0)}>{fmtAvgPnl(data.avg_win, data.wins)}</span>, avg
        loss <span className={pnlClass(data.avg_loss || 0)}>{fmtAvgPnl(data.avg_loss, data.losses)}</span>, PnL{" "}
        <span className={pnlClass(data.pnl || 0)}>{fmtCurrency(data.pnl || 0)}</span>. Defaults to
        today; clear dates for the full log.
      </Text>
    );
  }

  renderByStrategy(rows) {
    const by = {};
    rows.forEach((r) => {
      const sid = String(r.strategy_id || "").split(",")[0].trim() || "(unknown)";
      if (!by[sid]) by[sid] = { strategy_id: sid, trades: 0, wins: 0, losses: 0, pnl: 0, winPnl: 0, lossPnl: 0 };
      const pnl = Number(r.pnl || 0);
      by[sid].trades += 1;
      by[sid].pnl += pnl;
      if (r.won) {
        by[sid].wins += 1;
        by[sid].winPnl += pnl;
      } else {
        by[sid].losses += 1;
        by[sid].lossPnl += pnl;
      }
    });
    const list = Object.values(by).sort((a, b) => a.pnl - b.pnl);
    if (list.length === 0) return null;
    return (
      <Card className="mb-3">
        <Card.Header>
          <Card.Title>P&L by strategy</Card.Title>
        </Card.Header>
        <Table responsive size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColHeader>Strategy</Table.ColHeader>
              <Table.ColHeader>Trades</Table.ColHeader>
              <Table.ColHeader>Wins</Table.ColHeader>
              <Table.ColHeader>Losses</Table.ColHeader>
              <Table.ColHeader>Avg win</Table.ColHeader>
              <Table.ColHeader>Avg loss</Table.ColHeader>
              <Table.ColHeader>PnL</Table.ColHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {list.map((r) => (
              <Table.Row key={r.strategy_id}>
                <Table.Col>{r.strategy_id}</Table.Col>
                <Table.Col>{r.trades}</Table.Col>
                <Table.Col className="text-success">{r.wins}</Table.Col>
                <Table.Col className="text-danger">{r.losses}</Table.Col>
                <Table.Col className={pnlClass(r.wins ? r.winPnl / r.wins : 0)}>
                  {fmtAvgPnl(r.wins ? r.winPnl / r.wins : 0, r.wins)}
                </Table.Col>
                <Table.Col className={pnlClass(r.losses ? r.lossPnl / r.losses : 0)}>
                  {fmtAvgPnl(r.losses ? r.lossPnl / r.losses : 0, r.losses)}
                </Table.Col>
                <Table.Col className={pnlClass(r.pnl)}>{fmtCurrency(r.pnl)}</Table.Col>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Card>
    );
  }

  renderClosed(rows) {
    if (rows.length === 0) {
      return <Alert type="info">No closed round-trips for this filter.</Alert>;
    }
    return (
      <Card>
        <Card.Header>
          <Card.Title>{rows.length} closed trades</Card.Title>
        </Card.Header>
        <Table responsive highlightRowOnHover>
          <Table.Header>
            <Table.Row>
              <Table.ColHeader>Exit</Table.ColHeader>
              <Table.ColHeader>Strategy</Table.ColHeader>
              <Table.ColHeader>Symbol</Table.ColHeader>
              <Table.ColHeader>Side</Table.ColHeader>
              <Table.ColHeader>Qty</Table.ColHeader>
              <Table.ColHeader>Entry</Table.ColHeader>
              <Table.ColHeader>Exit px</Table.ColHeader>
              <Table.ColHeader>PnL</Table.ColHeader>
              <Table.ColHeader>Result</Table.ColHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.map((r, i) => (
              <Table.Row key={`${r.symbol}-${r.exit_ts}-${i}`}>
                <Table.Col>{fmtDateTime(r.exit_ts)}</Table.Col>
                <Table.Col>{r.strategy_id || "-"}</Table.Col>
                <Table.Col>
                  {r.symbol}
                  <div className="text-muted small">{fmtDateTime(r.entry_ts)}</div>
                </Table.Col>
                <Table.Col>{r.side}</Table.Col>
                <Table.Col>{r.quantity}</Table.Col>
                <Table.Col>{Number(r.entry_price || 0).toFixed(2)}</Table.Col>
                <Table.Col>{Number(r.exit_price || 0).toFixed(2)}</Table.Col>
                <Table.Col className={pnlClass(Number(r.pnl || 0))}>{fmtCurrency(r.pnl || 0)}</Table.Col>
                <Table.Col className={r.won ? "text-success" : "text-danger"}>
                  {r.won ? "WIN" : "LOSS"}
                </Table.Col>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Card>
    );
  }

  renderFills(fills) {
    if (fills.length === 0) {
      return <Alert type="info">No original fills for this filter.</Alert>;
    }
    return (
      <Card>
        <Card.Header>
          <Card.Title>{fills.length} original fills</Card.Title>
        </Card.Header>
        <Table responsive highlightRowOnHover>
          <Table.Header>
            <Table.Row>
              <Table.ColHeader>Time</Table.ColHeader>
              <Table.ColHeader>Strategy</Table.ColHeader>
              <Table.ColHeader>Symbol</Table.ColHeader>
              <Table.ColHeader>Side</Table.ColHeader>
              <Table.ColHeader>Qty</Table.ColHeader>
              <Table.ColHeader>Price</Table.ColHeader>
              <Table.ColHeader>Order</Table.ColHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {fills.map((r, i) => (
              <Table.Row key={`${r.order_id}-${i}`}>
                <Table.Col>{fmtDateTime(r.timestamp)}</Table.Col>
                <Table.Col>{r.strategy_id || "-"}</Table.Col>
                <Table.Col>{r.symbol}</Table.Col>
                <Table.Col className={r.transaction_type === "BUY" ? "text-success" : "text-danger"}>
                  {r.transaction_type}
                </Table.Col>
                <Table.Col>{r.quantity}</Table.Col>
                <Table.Col>{Number(r.price || 0).toFixed(2)}</Table.Col>
                <Table.Col>{r.order_id}</Table.Col>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Card>
    );
  }

  render() {
    const { loading, error, data, dateFrom, dateTo, view, strategyId } = this.state;
    const allRows = (data && data.rows) || [];
    const allFills = (data && data.fills) || [];
    const rows = strategyId
      ? allRows.filter((r) => String(r.strategy_id || "").split(",")[0].trim() === strategyId)
      : allRows;
    const fills = strategyId
      ? allFills.filter((r) => String(r.strategy_id || "").split(",")[0].trim() === strategyId)
      : allFills;
    const strategyOptions = [];
    const seen = {};
    allRows.concat(allFills).forEach((r) => {
      const sid = String(r.strategy_id || "").split(",")[0].trim();
      if (sid && !seen[sid]) {
        seen[sid] = true;
        strategyOptions.push(sid);
      }
    });
    strategyOptions.sort();
    return (
      <TradesSiteWrapper>
        <Page.Content
          title="Trades"
          subTitle="Closed broker round-trips with strategy P&L — toggle for original fills."
          options={<RefreshButton onClick={this.load} loading={loading} />}
        >
          <Alert type="info">
            <span data-page-note="1">
              These rows are your broker fills and closed round trips. Another trader&apos;s book is not shown here.
            </span>
          </Alert>
          {error ? <Alert type="danger">{error}</Alert> : null}
          <Card>
            <Card.Body>
              <Grid.Row>
                <Grid.Col md={3}>
                  <Form.Group label="From date">
                    <Form.Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => this.setState({ dateFrom: e.target.value })}
                    />
                  </Form.Group>
                </Grid.Col>
                <Grid.Col md={3}>
                  <Form.Group label="To date">
                    <Form.Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => this.setState({ dateTo: e.target.value })}
                    />
                  </Form.Group>
                </Grid.Col>
                <Grid.Col md={3}>
                  <Form.Group label="Strategy">
                    <Form.Select
                      value={strategyId}
                      onChange={(e) => this.setState({ strategyId: e.target.value })}
                    >
                      <option value="">All strategies</option>
                      {strategyOptions.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Grid.Col>
                <Grid.Col md={3}>
                  <Form.Group label={"\u00a0"}>
                    <div className="d-flex" style={{ gap: 8, flexWrap: "wrap" }}>
                      <Button color="primary" onClick={this.load} disabled={loading}>
                        Apply filter
                      </Button>
                      <Button color="secondary" outline onClick={this.setToday} disabled={loading}>
                        Today
                      </Button>
                      <Button color="secondary" outline onClick={this.clearDates} disabled={loading}>
                        All
                      </Button>
                      <Button
                        color={view === "closed" ? "primary" : "secondary"}
                        outline={view !== "closed"}
                        onClick={() => this.setState({ view: "closed" })}
                        disabled={loading}
                      >
                        Closed P&L
                      </Button>
                      <Button
                        color={view === "fills" ? "primary" : "secondary"}
                        outline={view !== "fills"}
                        onClick={() => this.setState({ view: "fills" })}
                        disabled={loading}
                      >
                        Original fills
                      </Button>
                    </div>
                  </Form.Group>
                </Grid.Col>
              </Grid.Row>
            </Card.Body>
          </Card>
          {loading ? <Text>Loading trades…</Text> : null}
          {!loading && !error ? (
            <React.Fragment>
              {view === "closed" ? this.renderSummary(data) : null}
              {view === "closed" ? this.renderByStrategy(rows) : null}
              {view === "closed" ? this.renderClosed(rows) : this.renderFills(fills)}
            </React.Fragment>
          ) : null}
        </Page.Content>
      </TradesSiteWrapper>
    );
  }
}

export default TradesPage;
